import { useMemo, useState, useEffect, useCallback } from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";
import { Route, Switch, Redirect } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Typography,
  Banner,
  Table,
  useTableQuery,
  TabProvider,
  TabsNavigation,
  useTheme,
  HStack,
  ColumnOptions,
} from "@smartrent/ui";
import { FlagSolid, Community, ChevronRight } from "@smartrent/icons";

import Layout from "@/layout/Layout";
import { useUser } from "@/layout/Context";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useUrlFilters, FilterInputValue } from "@/hooks/url-filters";
import { useOrganizationsQuery } from "@/api/organizations";
import { instance } from "@/lib/hooks";

import { fetchFeatureFlags } from "./api";
import {
  TableFeatureFlag,
  TableOrganizationFlags,
  OrganizationHierarchyResponse,
} from "./types";
import {
  useCMWFeatureFlagHandlers,
  useOrganizationFlagsHandlers,
} from "./hooks";
import { useFilterOptions } from "./useFilterOptions";

const tabs = [
  {
    id: "all-flags",
    label: "All Feature Flags",
    Icon: FlagSolid,
    to: "/feature-flags",
  },
  {
    id: "by-organization",
    label: "Flags by Organization",
    Icon: Community,
    to: "/feature-flags/by-organization",
  },
];

function AllFeatureFlagsView() {
  const { handleRowPress } = useCMWFeatureFlagHandlers();
  const { organizationOptions, groupOptions } = useFilterOptions();

  const tableProps = useTableQuery({
    columns: [
      {
        name: "displayName",
        header: "Name",
        render: ({ row }: { row: TableFeatureFlag }) => (
          <Typography type="body">{row.displayName}</Typography>
        ),
        sortable: true,
        filterType: {
          type: "textInputField",
        },
      },
      {
        name: "globallyEnabled",
        header: "Globally Enabled",
        render: ({ row }: { row: TableFeatureFlag }) => (
          <Typography color={row.globallyEnabled ? "success" : "error"}>
            {row.globallyEnabled ? "Yes" : "No"}
          </Typography>
        ),
        sortable: true,
        filterType: {
          type: "selectField",
          options: [
            { label: "Yes", value: "true" },
            { label: "No", value: "false" },
          ],
        },
      },
      {
        name: "enabledOrganizations",
        header: "Organizations",
        render: ({ row }: { row: TableFeatureFlag }) => (
          <Typography>{row.enabledOrganizations.length}</Typography>
        ),
        sortable: true,
      },
      {
        name: "enabledGroups",
        header: "Groups",
        render: ({ row }: { row: TableFeatureFlag }) => (
          <Typography>{row.enabledGroups.length}</Typography>
        ),
        sortable: true,
      },
      {
        name: "organization",
        header: "Organization",
        render: () => null,
        hidden: true,
        filterType: {
          type: "multiSelectField",
          options: organizationOptions,
        },
      },
      {
        name: "group",
        header: "Group",
        render: () => null,
        hidden: true,
        filterType: {
          type: "multiSelectField",
          options: groupOptions,
        },
      },
    ],
    fetch: fetchFeatureFlags,
    getQueryKey: ({ filters, page, pageSize, sortColumn, sortDirection }) => {
      return [
        "cmw-feature-flags-table",
        {},
        {
          ...filters,
          page,
          limit: pageSize,
          sort: sortColumn,
          dir: sortDirection,
        },
      ];
    },
    defaultPageSize: 2000,
  });

  return (
    <View style={styles.tableContainer}>
      <Table
        title="CMW Feature Flags"
        noRecordsText="No feature flags found"
        onRowPress={handleRowPress}
        {...tableProps}
      />
    </View>
  );
}

function FlagsByOrganizationView() {
  const { permissions } = useUser();
  const { handleOrgFlagsPress, handleGroupFlagsPress, handleUserFlagsPress } =
    useOrganizationFlagsHandlers();

  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const { filters, setAllFilters } = useUrlFilters({
    keys: ["name", "page", "sort", "dir"],
  });

  const { data: orgsData, isLoading: orgsLoading } = useOrganizationsQuery({
    filters,
  });

  const { data: hierarchyData, isLoading: flagsLoading } = useQuery({
    queryKey: ["cmw-feature-flags-organization-hierarchy"],
    queryFn: async () => {
      const response = await instance.get<OrganizationHierarchyResponse>(
        "/cmw-feature-flags/organization-hierarchy"
      );
      return response.data;
    },
  });

  useEffect(() => {
    if (orgsData?.records) {
      setTotalPages(orgsData?.total_pages ?? 1);
      setTotalRecords(orgsData?.total_records ?? 0);
    }
  }, [orgsData]);

  const flagCounts = useMemo(() => {
    if (!hierarchyData) {
      return {
        orgFlags: new Map<number, number>(),
        groupFlags: new Map<number, number>(),
        userFlags: new Map<number, number>(),
      };
    }

    const allOrgFlags =
      hierarchyData.organizations["all"]?.organization_level_flags || [];
    const allGroupFlags =
      hierarchyData.organizations["all"]?.group_level_flags || [];
    const allUserFlags =
      hierarchyData.organizations["all"]?.user_level_flags || [];

    const { orgFlags, groupFlags, userFlags } = Object.keys(
      hierarchyData.organizations
    )
      .filter((orgIdStr) => orgIdStr !== "all")
      .reduce(
        (acc, orgIdStr) => {
          const orgId = Number(orgIdStr);
          const orgData = hierarchyData.organizations[orgIdStr];

          acc.orgFlags.set(
            orgId,
            (orgData.organization_level_flags?.length || 0) + allOrgFlags.length
          );
          acc.groupFlags.set(
            orgId,
            (orgData.group_level_flags?.length || 0) + allGroupFlags.length
          );
          acc.userFlags.set(
            orgId,
            (orgData.user_level_flags?.length || 0) + allUserFlags.length
          );

          return acc;
        },
        {
          orgFlags: new Map<number, number>(),
          groupFlags: new Map<number, number>(),
          userFlags: new Map<number, number>(),
        }
      );

    return { orgFlags, groupFlags, userFlags };
  }, [hierarchyData]);

  const transformedData: TableOrganizationFlags[] = useMemo(() => {
    if (!orgsData?.records) {
      return [];
    }

    const allOrgFlagsCount =
      hierarchyData?.organizations["all"]?.organization_level_flags?.length ||
      0;
    const allGroupFlagsCount =
      hierarchyData?.organizations["all"]?.group_level_flags?.length || 0;
    const allUserFlagsCount =
      hierarchyData?.organizations["all"]?.user_level_flags?.length || 0;

    return orgsData.records.map((org) => {
      const orgId = Number(org.id);
      return {
        id: orgId,
        organizationId: orgId,
        organizationName: org.name,
        orgFlagsCount: flagCounts.orgFlags.get(orgId) ?? allOrgFlagsCount,
        groupFlagsCount: flagCounts.groupFlags.get(orgId) ?? allGroupFlagsCount,
        userFlagsCount: flagCounts.userFlags.get(orgId) ?? allUserFlagsCount,
      };
    });
  }, [orgsData?.records, flagCounts, hierarchyData]);

  const columns: ColumnOptions<TableOrganizationFlags>[] = useMemo(() => {
    const baseColumns: ColumnOptions<TableOrganizationFlags>[] = [
      {
        name: "organizationName",
        header: "Organization Name",
        render: ({ row }: { row: TableOrganizationFlags }) => (
          <Typography type="body">{row.organizationName}</Typography>
        ),
        sortable: true,
        filterType: {
          type: "textInputField",
        },
      },
      {
        name: "orgFlagsCount",
        header: "Org Flags",
        render: ({ row }: { row: TableOrganizationFlags }) => (
          <TouchableOpacity
            onPress={() => handleOrgFlagsPress(row)}
            style={styles.clickableCell}
            activeOpacity={0.7}
          >
            <HStack spacing={8} align="center">
              <Typography type="body">{row.orgFlagsCount}</Typography>
              <ChevronRight size={16} />
            </HStack>
          </TouchableOpacity>
        ),
        sortable: false,
      },
      {
        name: "groupFlagsCount",
        header: "Group Flags",
        render: ({ row }: { row: TableOrganizationFlags }) => (
          <TouchableOpacity
            onPress={() => handleGroupFlagsPress(row)}
            style={styles.clickableCell}
            activeOpacity={0.7}
          >
            <HStack spacing={8} align="center">
              <Typography type="body">{row.groupFlagsCount}</Typography>
              <ChevronRight size={16} />
            </HStack>
          </TouchableOpacity>
        ),
        sortable: false,
      },
    ];

    if (permissions.manage_user_level_feature_flags) {
      baseColumns.push({
        name: "userFlagsCount",
        header: "User Flags",
        render: ({ row }: { row: TableOrganizationFlags }) => (
          <TouchableOpacity
            onPress={() => handleUserFlagsPress(row)}
            style={styles.clickableCell}
            activeOpacity={0.7}
          >
            <HStack spacing={8} align="center">
              <Typography type="body">{row.userFlagsCount}</Typography>
              <ChevronRight size={16} />
            </HStack>
          </TouchableOpacity>
        ),
        sortable: false,
      });
    }

    return baseColumns;
  }, [
    handleOrgFlagsPress,
    handleGroupFlagsPress,
    handleUserFlagsPress,
    permissions,
  ]);

  const keyExtractor = useCallback(
    (row: TableOrganizationFlags) => `${row.id}`,
    []
  );

  const currentPage = Number(filters?.page) || 1;
  const isLoading = orgsLoading || flagsLoading;

  const fixedFilters = useMemo(
    () => ({
      ...filters,
      organizationName: filters?.name ?? "",
    }),
    [filters]
  );

  const handleFiltersChange = useCallback(
    (newFilters: Record<string, unknown>) => {
      const nameChanged = newFilters.organizationName !== filters?.name;
      const filtersToSet: Record<
        "sort" | "name" | "page" | "dir",
        FilterInputValue
      > = {
        ...filters,
        ...newFilters,
        name:
          (newFilters.organizationName as string | undefined) ??
          filters?.name ??
          "",
        page: nameChanged
          ? 1
          : (newFilters.page as number | undefined) ?? filters?.page ?? 1,
        sort: (newFilters.sort as string | undefined) ?? filters?.sort ?? "",
        dir: (newFilters.dir as string | undefined) ?? filters?.dir ?? "",
      };

      delete (filtersToSet as any).organizationName;

      setAllFilters(filtersToSet);
    },
    [setAllFilters, filters]
  );

  return (
    <View style={styles.tableContainer}>
      <Table
        title="Flags by Organization"
        noRecordsText="No organizations found"
        isRowPressable={() => false}
        loading={isLoading}
        data={transformedData}
        totalRecords={totalRecords}
        totalPages={totalPages}
        currentPage={currentPage}
        columns={columns}
        keyExtractor={keyExtractor}
        filters={fixedFilters}
        onFiltersChange={handleFiltersChange}
        {...({ tableClearFiltersAction: true } as any)}
        onPageChange={(page) =>
          setAllFilters({
            ...filters,
            page,
          })
        }
        onSortChange={(sort, direction) => {
          const apiSort = sort === "organizationName" ? "name" : sort || "";
          setAllFilters({
            ...filters,
            sort: apiSort,
            dir: direction || "",
          });
        }}
        sortColumn={
          filters?.sort === "name"
            ? "organizationName"
            : (filters?.sort as string | undefined)
        }
        sortDirection={filters?.dir as "asc" | "desc" | undefined}
      />
    </View>
  );
}

export function CMWFeatureFlags() {
  useDocumentTitle("CMW Feature Flags");
  const { colors } = useTheme();

  return (
    <Layout>
      <TabProvider>
        <View>
          <View style={styles.m16}>
            <Banner
              title="Once globally enabled, all orgs/groups will be able to see this feature in CMW within 10 minutes UNLESS that flag is specifically disabled for that org/group."
              color="info"
            />
          </View>
          <View style={{ backgroundColor: colors.backgroundPrimary }}>
            <TabsNavigation items={tabs} />
          </View>
          <Switch>
            <Route exact path="/feature-flags">
              <AllFeatureFlagsView />
            </Route>
            <Route path="/feature-flags/by-organization">
              <FlagsByOrganizationView />
            </Route>
            <Route exact path="/feature-flags/*">
              <Redirect to="/feature-flags" />
            </Route>
          </Switch>
        </View>
      </TabProvider>
    </Layout>
  );
}

const styles = StyleSheet.create({
  m16: {
    marginHorizontal: 16,
    marginVertical: 8,
  },
  tableContainer: {
    flex: 1,
    margin: 16,
  },
  clickableCell: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
  },
});
